/**
 * LeadTriageLayout — tab shell that groups Lead Triage + All Leads under a
 * single "Lead Triage" sidebar entry.
 *
 * Triage (/triage) and All Leads (/leads) don't share a URL prefix, so this
 * layout accepts its child page as a `children` prop rather than using
 * <Outlet/>. Each route wraps itself:
 *   <Route path="/triage" element={<LeadTriageLayout><TriagePage/></LeadTriageLayout>}/>
 *   <Route path="/leads"  element={<LeadTriageLayout><AllLeadsPage/></LeadTriageLayout>}/>
 *
 * Layout.jsx has an explicit /triage active-state matcher that also matches
 * /leads, so the sidebar entry stays highlighted on both.
 */

import { NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePresentationChrome } from "../hooks/usePresentationChrome";

const BASE_TABS = [
  { to: "/triage", label: "Triage", permission: "leads.manual_screen_1" },
  { to: "/leads", label: "All Leads", permission: "leads.view" },
  { to: "/leads/attachments", label: "Attachments", permission: "admin.data" },
  { to: "/leads/trust", label: "Lead Trust", permission: "admin.settings" },
];

export default function LeadTriageLayout({ children }) {
  const { hasPermission } = useAuth();
  const tabs = BASE_TABS.filter((t) => !t.permission || hasPermission(t.permission));

  // Presentation Mode (toggled from TriageCenter, several levels below this
  // wrapper) hides this tab strip too — see usePresentationChrome. Exiting
  // is via TriageCenter's floating "Exit Presentation Mode" button.
  const { presentationMode } = usePresentationChrome();
  const hideOwnChrome = presentationMode;

  return (
    <div className="flex flex-col h-full">
      <div
        className={`flex-shrink-0 transition-[max-height,opacity] duration-200 ${
          hideOwnChrome
            ? "max-h-0 opacity-0 pointer-events-none overflow-hidden"
            : "max-h-14 opacity-100 overflow-visible"
        }`}
      >
        <div className="flex items-center gap-1 px-4 border-b border-neutral-200 bg-white overflow-x-auto">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-neutral-400 mr-3 whitespace-nowrap">
            Lead Triage
          </span>
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end
              className={({ isActive }) =>
                `px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "border-[#09244B] text-[#09244B] font-semibold"
                    : "border-transparent text-neutral-500 font-medium hover:text-neutral-800 hover:border-neutral-300"
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
    </div>
  );
}
